/**
 * Rich-text editor for product descriptions.
 *
 * Toolbar: paragraph, headings (H2/H3), bold, italic, underline,
 * bullet/ordered lists, TEXT COLOR (full palette + gray shades + custom HEX),
 * and TEXT HIGHLIGHT / background color (palette + custom HEX + remove).
 *
 * Output is clean HTML via TipTap; the backend bleach allow-list in
 * /app/backend/html_sanitize.py permits <span style="color:…"> and
 * <mark style="background-color:…"> — anything else is stripped on save.
 */
import React, { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Highlight } from "@tiptap/extension-highlight";
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading2, Heading3, Pilcrow, Baseline, Highlighter, X, Truck } from "lucide-react";

// Reusable freight-glass shipping/delivery template. Wording aligned with the
// site Shipping Policy (processing time 1–3 business days). Inserted on demand
// only — never auto-inserted when opening a product.
const FREIGHT_TEMPLATE_MARKER = "Important Delivery Instructions";
const FREIGHT_TEMPLATE_HTML = `<p><strong>Shipping &amp; Important Delivery Instructions</strong></p><ol><li><strong>Fitment Verification:</strong> Please provide your vehicle's VIN before ordering so we can help verify compatibility with the glass being purchased.</li><li><strong>Safe Transit:</strong> Your glass is securely packaged for freight transportation. Handling time is typically 1–3 business days, subject to product availability and order requirements.</li><li><strong>Mandatory Inspection:</strong> You MUST inspect the glass and packaging for damage before signing the delivery receipt. Document any visible damage immediately.</li><li><strong>If Damaged:</strong> If the glass is damaged, refuse delivery where appropriate and contact Lecis Auto Glass immediately for assistance. Follow our Shipping Policy for damage-reporting instructions.</li></ol><p><strong>Can't Find Your Part?</strong> We have access to additional automotive glass products that may not yet be listed on our website. Contact Lecis Auto Glass for availability and fitment assistance.</p>`;

function insertFreightTemplate(editor) {
  if (!editor) return;
  // Guard against accidental duplicate insertion.
  if (editor.getHTML().includes(FREIGHT_TEMPLATE_MARKER)) return;
  editor.chain().focus().insertContent(FREIGHT_TEMPLATE_HTML).run();
}

// Text color palette — gray shades first, then a full color spectrum.
const TEXT_GRAYS = ["#000000", "#374151", "#6b7280", "#9ca3af", "#d1d5db", "#ffffff"];
const TEXT_COLORS = [
  "#dc2626", "#ea580c", "#d97706", "#ca8a04", "#65a30d", "#16a34a",
  "#0d9488", "#0891b2", "#2563eb", "#4f46e5", "#7c3aed", "#c026d3", "#db2777",
];
// Highlight (background) palette — soft tints that keep dark text readable.
const HIGHLIGHT_COLORS = [
  "#fef08a", "#fed7aa", "#fecaca", "#bbf7d0", "#a5f3fc", "#bfdbfe", "#e9d5ff", "#fbcfe8", "#e5e7eb",
];

export default function RichTextEditor({ value, onChange, placeholder = "Describe the product…" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === "<p></p>" ? "" : html);
    },
    editorProps: {
      attributes: {
        class: "min-h-[180px] max-h-[400px] overflow-y-auto px-3 py-2 text-sm border border-zinc-300 rounded-sm focus:outline-none focus:border-red-500 bg-white prose prose-sm max-w-none",
        "data-testid": "form-description-editor",
        "data-placeholder": placeholder,
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    if (incoming && incoming !== current && incoming !== "<p></p>") {
      editor.commands.setContent(incoming, false);
    }
  }, [editor, value]);

  if (!editor) {
    return <div className="min-h-[180px] border border-zinc-300 rounded-sm bg-zinc-50" />;
  }

  return (
    <div data-testid="rich-text-editor">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
}

function Toolbar({ editor }) {
  const btn = (active) =>
    `inline-flex items-center justify-center w-8 h-8 rounded text-zinc-700 hover:bg-zinc-100 ${active ? "bg-zinc-200" : ""}`;
  return (
    <div className="flex flex-wrap items-center gap-1 mb-2 p-1 border border-zinc-200 rounded-sm bg-zinc-50" data-testid="rich-text-toolbar">
      <button type="button" data-testid="rt-paragraph" onClick={() => editor.chain().focus().setParagraph().run()} className={btn(editor.isActive("paragraph"))} title="Paragraph">
        <Pilcrow size={14} />
      </button>
      <button type="button" data-testid="rt-h2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={btn(editor.isActive("heading", { level: 2 }))} title="Heading 2">
        <Heading2 size={14} />
      </button>
      <button type="button" data-testid="rt-h3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={btn(editor.isActive("heading", { level: 3 }))} title="Heading 3">
        <Heading3 size={14} />
      </button>
      <div className="w-px h-5 bg-zinc-300 mx-1" />
      <button type="button" data-testid="rt-bold" onClick={() => editor.chain().focus().toggleBold().run()} className={btn(editor.isActive("bold"))} title="Bold">
        <Bold size={14} />
      </button>
      <button type="button" data-testid="rt-italic" onClick={() => editor.chain().focus().toggleItalic().run()} className={btn(editor.isActive("italic"))} title="Italic">
        <Italic size={14} />
      </button>
      <button type="button" data-testid="rt-underline" onClick={() => editor.chain().focus().toggleUnderline().run()} className={btn(editor.isActive("underline"))} title="Underline">
        <UnderlineIcon size={14} />
      </button>
      <div className="w-px h-5 bg-zinc-300 mx-1" />
      <button type="button" data-testid="rt-ul" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btn(editor.isActive("bulletList"))} title="Bullet list">
        <List size={14} />
      </button>
      <button type="button" data-testid="rt-ol" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btn(editor.isActive("orderedList"))} title="Numbered list">
        <ListOrdered size={14} />
      </button>
      <div className="w-px h-5 bg-zinc-300 mx-1" />

      <ColorMenu
        editor={editor}
        testid="rt-text-color"
        label="Text color"
        icon={<Baseline size={14} />}
        grays={TEXT_GRAYS}
        colors={TEXT_COLORS}
        onPick={(c) => editor.chain().focus().setColor(c).run()}
        onClear={() => editor.chain().focus().unsetColor().run()}
        prefix="text"
      />
      <ColorMenu
        editor={editor}
        testid="rt-highlight"
        label="Highlight"
        icon={<Highlighter size={14} />}
        colors={HIGHLIGHT_COLORS}
        onPick={(c) => editor.chain().focus().toggleHighlight({ color: c }).run()}
        onClear={() => editor.chain().focus().unsetHighlight().run()}
        prefix="hl"
      />
      <div className="w-px h-5 bg-zinc-300 mx-1" />
      <button
        type="button"
        data-testid="rt-insert-freight"
        onClick={() => insertFreightTemplate(editor)}
        title="Insert freight glass shipping & delivery instructions"
        className="inline-flex items-center gap-1 h-8 px-2 rounded text-xs text-zinc-700 hover:bg-zinc-100"
      >
        <Truck size={14} /> Insert Freight Instructions
      </button>
    </div>
  );
}

function ColorMenu({ testid, label, icon, grays, colors, onPick, onClear, prefix }) {
  const [open, setOpen] = useState(false);
  const [hex, setHex] = useState("#000000");
  const swatch = (c) => (
    <button
      key={c}
      type="button"
      data-testid={`${prefix}-color-${c.replace("#", "")}`}
      onClick={() => { onPick(c); setOpen(false); }}
      title={c}
      className="w-5 h-5 rounded-full border border-zinc-300 hover:scale-110 transition-transform"
      style={{ backgroundColor: c }}
    />
  );
  return (
    <div className="relative">
      <button
        type="button"
        data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        title={label}
        className="inline-flex items-center justify-center w-8 h-8 rounded text-zinc-700 hover:bg-zinc-100"
      >
        {icon}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            data-testid={`${testid}-menu`}
            className="absolute z-20 mt-1 left-0 w-56 p-3 bg-white border border-zinc-200 rounded-md shadow-lg"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">{label}</span>
              <button type="button" data-testid={`${prefix}-color-clear`} onClick={() => { onClear(); setOpen(false); }} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-900">
                <X size={11} /> Remove
              </button>
            </div>
            {grays && (
              <div className="flex flex-wrap gap-1.5 mb-2 pb-2 border-b border-zinc-100">
                {grays.map(swatch)}
              </div>
            )}
            <div className="flex flex-wrap gap-1.5 mb-3">
              {colors.map(swatch)}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="color"
                data-testid={`${prefix}-color-picker`}
                value={hex}
                onChange={(e) => setHex(e.target.value)}
                className="w-8 h-8 p-0 border border-zinc-300 rounded cursor-pointer bg-white"
                title="Pick a custom color"
              />
              <input
                type="text"
                data-testid={`${prefix}-color-hex`}
                value={hex}
                onChange={(e) => setHex(e.target.value)}
                placeholder="#RRGGBB"
                className="flex-1 min-w-0 px-2 py-1 text-xs border border-zinc-300 rounded font-mono"
              />
              <button
                type="button"
                data-testid={`${prefix}-color-apply`}
                onClick={() => { if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) { onPick(hex); setOpen(false); } }}
                className="px-2 py-1 text-[10px] uppercase tracking-[0.15em] font-bold bg-zinc-900 text-white rounded hover:bg-zinc-700"
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
